<!-- Loading -->
<div *ngIf="loading" class="profile-loading">
  <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
  <p>Loading profile…</p>
</div>

<!-- Error -->
<div *ngIf="error && !loading" class="profile-error">
  <mat-icon>error_outline</mat-icon>
  <p>{{ error }}</p>
  <button mat-stroked-button (click)="loadUserProfile()">Retry</button>
</div>

<!-- Content -->
<div *ngIf="!loading && !error" class="profile-page">

  <!-- Page header -->
  <div class="profile-header">
    <div class="profile-header-text">
      <h1 class="profile-title">{{ userProfile?.name || '—' }}</h1>
      <p class="profile-subtitle">{{ userProfile?.loginId }}</p>
    </div>
  </div>

  <!-- Tabs -->
  <mat-tab-group animationDuration="200ms" class="profile-tabs">

    <!-- Profile -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">person_outline</mat-icon> Profile
      </ng-template>

      <div class="tab-content">
        <mat-card>
          <mat-card-header><mat-card-title>Account</mat-card-title></mat-card-header>
          <mat-card-content>
            <div class="info-row"><span class="info-label">Name</span><span>{{ userProfile?.name || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Username</span><span>{{ userProfile?.loginId }}</span></div>
            <div class="info-row">
              <span class="info-label">Email</span>
              <a [href]="'mailto:' + userProfile?.loginId">{{ userProfile?.loginId }}</a>
            </div>
            <div class="info-row" *ngIf="userProfile?.isSystemUser">
              <span class="info-label">Account Type</span>
              <mat-chip [disableRipple]="true" class="system-chip">System User</mat-chip>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-card>
          <mat-card-header><mat-card-title>Preferences</mat-card-title></mat-card-header>
          <mat-card-content>

            <!-- Roles -->
            <div class="info-row chip-row">
              <span class="info-label">Roles</span>
              <mat-chip-set>
                <mat-chip *ngFor="let role of userProfile?.roles" [disableRipple]="true">{{ formatRoleName(role) }}</mat-chip>
                <mat-chip *ngIf="isAdhocUser" [disableRipple]="true">Guest</mat-chip>
              </mat-chip-set>
            </div>

            <!-- Teams -->
            <div class="info-row chip-row">
              <span class="info-label">Teams</span>
              <mat-chip-set *ngIf="userProfile?.teams?.length; else noTeams">
                <mat-chip *ngFor="let t of userProfile?.teams" [disableRipple]="true" class="team-chip">
                  {{ t.team }}<span *ngIf="t.isPrimary" class="primary-badge">&#9733;</span>
                </mat-chip>
              </mat-chip-set>
              <ng-template #noTeams><span class="no-data">—</span></ng-template>
            </div>

            <!-- Timezone -->
            <div class="info-row timezone-row">
              <span class="info-label">Timezone</span>
              <div class="timezone-value">
                <ng-container *ngIf="!isEditingTimezone">
                  <span>{{ displayTimezone }}</span>
                  <mat-chip *ngIf="isTimezoneOverridden" [disableRipple]="true" class="custom-chip">custom</mat-chip>
                  <button mat-icon-button matTooltip="Edit timezone" (click)="startEditTimezone()">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button mat-icon-button *ngIf="isTimezoneOverridden" matTooltip="Reset to browser locale" (click)="resetTimezone()">
                    <mat-icon>refresh</mat-icon>
                  </button>
                </ng-container>
                <ng-container *ngIf="isEditingTimezone">
                  <mat-form-field appearance="outline" class="tz-field" subscriptSizing="dynamic">
                    <mat-label>Timezone</mat-label>
                    <mat-select [(ngModel)]="pendingTimezone">
                      <mat-option *ngFor="let tz of timezones" [value]="tz.value">{{ tz.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <button mat-icon-button color="primary" matTooltip="Save" (click)="saveTimezone()">
                    <mat-icon>check</mat-icon>
                  </button>
                  <button mat-icon-button matTooltip="Cancel" (click)="cancelEditTimezone()">
                    <mat-icon>close</mat-icon>
                  </button>
                </ng-container>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <!-- Security -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">lock_outline</mat-icon> Security
      </ng-template>
      <div class="tab-content">
        <mat-card>
          <mat-card-header><mat-card-title>Password</mat-card-title></mat-card-header>
          <mat-card-content>
            <ng-container *ngIf="isAdhocUser">
              <p class="hint-text">Guest accounts cannot change passwords.</p>
            </ng-container>
            <ng-container *ngIf="!isAdhocUser">
              <p class="hint-text">Keep your account secure with a strong, unique password.</p>
              <button mat-raised-button color="primary" (click)="editPassword()">
                <mat-icon>key</mat-icon> Change Password
              </button>
            </ng-container>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

    <!-- Settings -->
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="tab-icon">settings</mat-icon> Settings
      </ng-template>
      <div class="tab-content">
        <mat-card>
          <mat-card-header><mat-card-title>User Preferences</mat-card-title></mat-card-header>
          <mat-card-content>
            <p class="hint-text">Reset all saved UI preferences — view modes, dismissed banners, timezone overrides and other per-feature settings will be cleared.</p>
            <button mat-stroked-button color="warn" [disabled]="isResettingPrefs" (click)="resetPreferences()">
              <mat-icon>restart_alt</mat-icon> Reset User Preferences
            </button>
          </mat-card-content>
        </mat-card>
      </div>
    </mat-tab>

  </mat-tab-group>
</div>

<!-- Change Password Dialog -->
<div *ngIf="showPasswordModal" class="cpw-backdrop" (click)="closePasswordModal()">
  <div class="cpw-dialog" (click)="$event.stopPropagation()">

    <div class="cpw-header">
      <mat-icon class="cpw-header-icon">lock_reset</mat-icon>
      <h3 class="cpw-title">Change Password</h3>
      <button mat-icon-button class="cpw-close-btn" (click)="closePasswordModal()" type="button">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="cpw-body">
      <form>
        <mat-form-field appearance="outline" class="cpw-field">
          <mat-label>New Password</mat-label>
          <mat-icon matPrefix>lock_outline</mat-icon>
          <input [type]="showNewPassword ? 'text' : 'password'" matInput
                 [(ngModel)]="passwordData.newPassword"
                 name="newPassword"
                 (input)="validatePassword()"
                 placeholder="Enter new password"
                 autocomplete="new-password">
          <button mat-icon-button matSuffix type="button"
                  (click)="showNewPassword = !showNewPassword"
                  [matTooltip]="showNewPassword ? 'Hide password' : 'Show password'">
            <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </mat-form-field>

        <mat-form-field appearance="outline" class="cpw-field">
          <mat-label>Confirm Password</mat-label>
          <mat-icon matPrefix>lock_outline</mat-icon>
          <input [type]="showConfirmPassword ? 'text' : 'password'" matInput
                 [(ngModel)]="passwordData.confirmPassword"
                 name="confirmPassword"
                 (input)="validatePassword()"
                 placeholder="Confirm new password"
                 autocomplete="new-password">
          <button mat-icon-button matSuffix type="button"
                  (click)="showConfirmPassword = !showConfirmPassword"
                  [matTooltip]="showConfirmPassword ? 'Hide password' : 'Show password'">
            <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </mat-form-field>

        <div class="cpw-requirements">
          <p class="cpw-req-title">Requirements</p>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.minLength">
            <mat-icon>{{ passwordValidation.minLength ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            At least 8 characters
          </div>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.hasUppercase">
            <mat-icon>{{ passwordValidation.hasUppercase ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            One uppercase letter
          </div>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.hasLowercase">
            <mat-icon>{{ passwordValidation.hasLowercase ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            One lowercase letter
          </div>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.hasNumber">
            <mat-icon>{{ passwordValidation.hasNumber ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            One number
          </div>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.hasSpecial">
            <mat-icon>{{ passwordValidation.hasSpecial ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            One special character
          </div>
          <div class="cpw-req-item" [class.cpw-req-valid]="passwordValidation.passwordsMatch">
            <mat-icon>{{ passwordValidation.passwordsMatch ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
            Passwords match
          </div>
        </div>

        <div *ngIf="passwordError" class="cpw-error">
          <mat-icon>error_outline</mat-icon> {{ passwordError }}
        </div>
        <div *ngIf="passwordSuccess" class="cpw-success">
          <mat-icon>check_circle</mat-icon> {{ passwordSuccess }}
        </div>
      </form>
    </div>

    <div class="cpw-footer">
      <button mat-stroked-button (click)="closePasswordModal()" type="button">Cancel</button>
      <button mat-raised-button color="primary" type="button"
              [disabled]="!isPasswordValid() || isUpdatingPassword"
              (click)="updatePassword()">
        <mat-progress-spinner *ngIf="isUpdatingPassword" mode="indeterminate" diameter="18" class="btn-spinner"></mat-progress-spinner>
        <mat-icon *ngIf="!isUpdatingPassword">lock_reset</mat-icon>
        {{ isUpdatingPassword ? 'Updating…' : 'Update Password' }}
      </button>
    </div>

  </div>
</div>
